// Frame buffer cache for managing pooled buffers across worklet boundaries
import { channelDataPool } from './channel-data-pool.js';

export interface FrameBuffer {
  id: string;
  channelData: Float32Array[][];
  timestamp: number;
  inUse: boolean;
}

export class FrameBufferCache {
  private buffers: Map<string, FrameBuffer> = new Map();
  private nextId = 0;

  /**
   * Checkout a frame buffer with pooled channel data
   */
  checkout(numChunks: number, channelCount: number): FrameBuffer {
    const id = `frame-${this.nextId++}`;
    const channelData = channelDataPool.checkout(numChunks, channelCount);

    const buffer: FrameBuffer = {
      id,
      channelData,
      timestamp: performance.now(),
      inUse: true
    };

    this.buffers.set(id, buffer);
    return buffer;
  }

  /**
   * Mark a frame buffer as returned from worklet
   */
  checkin(frameId: string): void {
    const buffer = this.buffers.get(frameId);
    if (buffer && buffer.inUse) {
      buffer.inUse = false;
      // Return channel data to pool
      channelDataPool.checkin(buffer.channelData);
      // Remove from cache after a delay to handle late returns
      setTimeout(() => {
        if (!buffer.inUse) {
          this.buffers.delete(frameId);
        }
      }, 1000);
    }
  }

  /**
   * Clean up stale buffers (not returned within timeout)
   */
  cleanup(timeoutMs: number = 5000): void {
    const now = performance.now();
    const staleBuffers: string[] = [];

    for (const [id, buffer] of this.buffers) {
      if (buffer.inUse && now - buffer.timestamp > timeoutMs) {
        // Return stale buffer to pool
        channelDataPool.checkin(buffer.channelData);
        staleBuffers.push(id);
      }
    }

    // Remove stale buffers
    staleBuffers.forEach(id => this.buffers.delete(id));

    if (staleBuffers.length > 0) {
      console.warn(`Cleaned up ${staleBuffers.length} stale frame buffers`);
    }
  }

  /**
   * Get statistics about the cache
   */
  getStatistics() {
    let inUseCount = 0;
    const totalCount = this.buffers.size;

    for (const buffer of this.buffers.values()) {
      if (buffer.inUse) {
        inUseCount++;
      }
    }

    return {
      totalBuffers: totalCount,
      inUseBuffers: inUseCount,
      availableBuffers: totalCount - inUseCount
    };
  }

  /**
   * Clear all buffers and return them to pool
   */
  clear(): void {
    for (const buffer of this.buffers.values()) {
      if (buffer.inUse) {
        channelDataPool.checkin(buffer.channelData);
      }
    }
    this.buffers.clear();
    this.nextId = 0;
  }
}

// Global instance
export const frameBufferCache = new FrameBufferCache();
